import React from "react";
import { motion } from "framer-motion";

interface RifathLogoProps {
  size?: number;
  animated?: boolean;
  className?: string;
}

const RifathLogo: React.FC<RifathLogoProps> = ({
  size = 40,
  animated = true,
  className = "",
}) => {
  // Unique ID prefix to avoid SVG ID collisions when multiple logos render
  const uid = React.useId().replace(/:/g, "");

  const Wrapper = animated ? motion.div : "div";
  const wrapperProps = animated
    ? {
        whileHover: { scale: 1.08 },
        transition: { type: "spring", stiffness: 300 },
      }
    : {};

  return (
    <Wrapper
      className={`relative flex-shrink-0 ${className}`}
      style={{ width: size, height: size }}
      {...(wrapperProps as any)}
    >
      <svg
        viewBox="0 0 100 100"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        className="w-full h-full"
        style={{ filter: "drop-shadow(0 2px 8px rgba(99,102,241,0.25))" }}
      >
        <defs>
          {/* Gradient for the border */}
          <linearGradient id={`${uid}-border`} x1="0" y1="0" x2="100" y2="100" gradientUnits="userSpaceOnUse">
            <stop offset="0%" stopColor="#3B82F6" />
            <stop offset="40%" stopColor="#8B5CF6" />
            <stop offset="100%" stopColor="#06B6D4" />
          </linearGradient>

          {/* Node glow */}
          <radialGradient id={`${uid}-node`}>
            <stop offset="0%" stopColor="white" stopOpacity="0.95" />
            <stop offset="100%" stopColor="white" stopOpacity="0.4" />
          </radialGradient>

          {/* Hexagonal-squircle clip for the image (modern, non-circular) */}
          <clipPath id={`${uid}-clip`}>
            <path d="M50 2 C72 2, 90 8, 96 22 C102 36, 98 64, 96 78 C94 88, 78 98, 50 98 C22 98, 6 88, 4 78 C2 64, -2 36, 4 22 C10 8, 28 2, 50 2Z" />
          </clipPath>

          {/* Inner clip (slightly smaller for image, creating border effect) */}
          <clipPath id={`${uid}-inner`}>
            <path d="M50 6 C70 6, 86 12, 92 24 C97 37, 94 63, 92 76 C90 85, 75 94, 50 94 C25 94, 10 85, 8 76 C6 63, 3 37, 8 24 C14 12, 30 6, 50 6Z" />
          </clipPath>
        </defs>

        {/* Gradient border shape */}
        <path
          d="M50 2 C72 2, 90 8, 96 22 C102 36, 98 64, 96 78 C94 88, 78 98, 50 98 C22 98, 6 88, 4 78 C2 64, -2 36, 4 22 C10 8, 28 2, 50 2Z"
          fill={`url(#${uid}-border)`}
        />

        {/* Profile image */}
        <g clipPath={`url(#${uid}-inner)`}>
          <image
            href="/profileimg_logo.png"
            x="0"
            y="0"
            width="100"
            height="100"
            preserveAspectRatio="xMidYMid slice"
          />
        </g>

        {/* Neural network connection lines on top */}
        <g opacity="0.35" stroke="white" strokeWidth="0.7" strokeLinecap="round">
          <line x1="8" y1="22" x2="20" y2="14" />
          <line x1="20" y1="14" x2="32" y2="20" />
          <line x1="68" y1="12" x2="82" y2="18" />
          <line x1="82" y1="18" x2="90" y2="32" />
          <line x1="8" y1="76" x2="18" y2="86" />
          <line x1="18" y1="86" x2="32" y2="82" />
          <line x1="72" y1="84" x2="86" y2="78" />
          <line x1="86" y1="78" x2="90" y2="32" />
        </g>

        {/* Neural network nodes */}
        <g>
          <circle cx="8" cy="22" r="2.2" fill={`url(#${uid}-node)`} opacity="0.6" />
          <circle cx="20" cy="14" r="1.6" fill={`url(#${uid}-node)`} opacity="0.5" />
          <circle cx="32" cy="20" r="1.8" fill={`url(#${uid}-node)`} opacity="0.45" />
          <circle cx="68" cy="12" r="1.6" fill={`url(#${uid}-node)`} opacity="0.45" />
          <circle cx="82" cy="18" r="2" fill={`url(#${uid}-node)`} opacity="0.55" />
          <circle cx="90" cy="32" r="1.8" fill={`url(#${uid}-node)`} opacity="0.5" />
          <circle cx="8" cy="76" r="1.8" fill={`url(#${uid}-node)`} opacity="0.5" />
          <circle cx="18" cy="86" r="2" fill={`url(#${uid}-node)`} opacity="0.55" />
          <circle cx="32" cy="82" r="1.6" fill={`url(#${uid}-node)`} opacity="0.45" />
          <circle cx="72" cy="84" r="1.8" fill={`url(#${uid}-node)`} opacity="0.5" />
          <circle cx="86" cy="78" r="2.2" fill={`url(#${uid}-node)`} opacity="0.6" />
        </g>

        {/* Subtle gradient overlay at bottom for depth */}
        <path
          d="M50 2 C72 2, 90 8, 96 22 C102 36, 98 64, 96 78 C94 88, 78 98, 50 98 C22 98, 6 88, 4 78 C2 64, -2 36, 4 22 C10 8, 28 2, 50 2Z"
          fill={`url(#${uid}-border)`}
          opacity="0.08"
        />
      </svg>

      {/* Ambient glow behind the logo */}
      {animated && (
        <div
          className="absolute inset-[-25%] bg-gradient-to-br from-blue-500 via-violet-500 to-cyan-500 blur-xl -z-10 opacity-25"
          style={{ borderRadius: "30%" }}
        />
      )}
    </Wrapper>
  );
};

export default RifathLogo;
